'use client';

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Bus, Users, TrendingUp, CheckCircle2, Clock, RefreshCw, Banknote, ShieldAlert } from 'lucide-react';
import { useOperationsDashboardRealtime } from '@/lib/hooks/useOperationsDashboardRealtime';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


const gradientMap: Record<string, string> = {
  alert: 'from-negative/10 to-negative/5',
  positive: 'from-positive/8 to-transparent',
  neutral: 'from-primary/8 to-transparent',
  warning: 'from-warning/10 to-warning/5',
};

const iconBg: Record<string, string> = {
  alert: 'bg-negative/15 text-negative',
  positive: 'bg-positive/15 text-positive',
  neutral: 'bg-primary/15 text-primary',
  warning: 'bg-warning/15 text-warning',
};

const valueColor: Record<string, string> = {
  alert: 'text-negative',
  positive: 'text-foreground',
  neutral: 'text-foreground',
  warning: 'text-warning',
};

const progressColor: Record<string, string> = {
  alert: 'bg-negative',
  positive: 'bg-positive',
  neutral: 'bg-primary',
  warning: 'bg-warning',
};

function LiveBadge({ isConnected, lastRefreshed }: { isConnected: boolean; lastRefreshed: Date | null }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/5 dark:bg-white/5 border border-[var(--glass-border)]">
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isConnected ? 'bg-positive animate-pulse' : 'bg-muted-foreground'
        }`}
      />
      <span className="text-[9px] font-700 uppercase tracking-wider text-muted-foreground">
        {isConnected ? 'Live' : 'Offline'}
      </span>
    </div>
  );
}

function ProgressBar({ value, max, colorClass }: { value: number; max: number; colorClass: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mt-3 h-1 rounded-full bg-black/8 dark:bg-white/8 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function KPIBentoGrid() {
  const { kpis, isConnected, isLoading, lastEvent } = useOperationsDashboardRealtime();

  const [pendingBhatta, setPendingBhatta] = useState<number | null>(null);
  const [violationsToday, setViolationsToday] = useState<number | null>(null);
  const [lastRefreshedLabel, setLastRefreshedLabel] = useState<string>('');

  useEffect(() => {
    if (kpis.lastRefreshed) {
      setLastRefreshedLabel(
        kpis.lastRefreshed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })
      );
    }
  }, [kpis.lastRefreshed]);

  useEffect(() => {
    const supabase = createClient();

    const fetchBhatta = async () => {
      const { count } = await supabase
        .from('bhatta_trips')
        .select('id', { count: 'exact', head: true })
        .in('bhatta_status', ['Pending', 'Calculated']);
      setPendingBhatta(count ?? 0);
    };

    const fetchViolations = async () => {
      const today = new Date().toISOString().split('T')[0];
      const { count } = await supabase
        .from('violations')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', `${today}T00:00:00`)
        .lte('created_at', `${today}T23:59:59`);
      setViolationsToday(count ?? 0);
    };

    fetchBhatta();
    fetchViolations();

    // Realtime refresh for bhatta_trips
    const bhattaChannel = supabase
      .channel('kpi_bhatta_trips')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bhatta_trips' }, fetchBhatta)
      .subscribe();

    // Realtime refresh for violations
    const violationsChannel = supabase
      .channel('kpi_violations_today')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'violations' }, fetchViolations)
      .subscribe();

    return () => {
      supabase.removeChannel(bhattaChannel);
      supabase.removeChannel(violationsChannel);
    };
  }, []);

  const fleetPct =
    kpis.fleetTotal > 0 ? Math.round((kpis.fleetUtilized / kpis.fleetTotal) * 100) : 0;
  const crewPct =
    kpis.crewTotal > 0 ? Math.round((kpis.crewAvailable / kpis.crewTotal) * 100) : 0;
  const completedPct =
    kpis.totalTodayDuties > 0
      ? Math.round((kpis.dutiesCompleted / kpis.totalTodayDuties) * 100)
      : 0;

  const unassignedVariant = kpis.unassignedDuties > 0 ? 'alert' : 'positive';
  const fleetVariant = fleetPct >= 80 ? 'positive' : fleetPct >= 50 ? 'neutral' : 'warning';
  const crewVariant = crewPct >= 50 ? 'positive' : crewPct >= 20 ? 'neutral' : 'warning';
  const bhattaVariant = (pendingBhatta ?? 0) > 10 ? 'warning' : (pendingBhatta ?? 0) > 0 ? 'neutral' : 'positive';
  const violationsVariant = (violationsToday ?? 0) > 5 ? 'alert' : (violationsToday ?? 0) > 0 ? 'warning' : 'positive';

  const kpiCards = [
    {
      id: 'kpi-unassigned',
      label: 'Unassigned Duties',
      value: isLoading ? '—' : String(kpis.unassignedDuties),
      sub: isLoading
        ? 'Loading…'
        : kpis.unassignedDuties === 0
        ? 'All duties assigned'
        : `${kpis.unassignedDuties} of ${kpis.totalTodayDuties} need crew`,
      icon: AlertTriangle,
      variant: unassignedVariant,
      span: 'col-span-1 sm:col-span-2',
      showProgress: false,
    },
    {
      id: 'kpi-fleet',
      label: 'Fleet Utilization',
      value: isLoading ? '—' : `${fleetPct}%`,
      sub: isLoading
        ? 'Loading…'
        : `${kpis.fleetUtilized} of ${kpis.fleetTotal} buses active`,
      icon: Bus,
      variant: fleetVariant,
      span: 'col-span-1',
      showProgress: true,
      progressValue: kpis.fleetUtilized,
      progressMax: kpis.fleetTotal,
    },
    {
      id: 'kpi-crew',
      label: 'Crew Available',
      value: isLoading ? '—' : String(kpis.crewAvailable),
      sub: isLoading
        ? 'Loading…'
        : `${crewPct}% of ${kpis.crewTotal} crew ready`,
      icon: Users,
      variant: crewVariant,
      span: 'col-span-1',
      showProgress: true,
      progressValue: kpis.crewAvailable,
      progressMax: kpis.crewTotal,
    },
    {
      id: 'kpi-active',
      label: 'Active Duties',
      value: isLoading ? '—' : String(kpis.dutiesActive),
      sub: isLoading
        ? 'Loading…'
        : `${kpis.dutiesScheduled} scheduled today`,
      icon: TrendingUp,
      variant: 'neutral' as const,
      span: 'col-span-1',
      showProgress: false,
    },
    {
      id: 'kpi-completed',
      label: 'Duties Completed',
      value: isLoading ? '—' : String(kpis.dutiesCompleted),
      sub: isLoading
        ? 'Loading…'
        : `${completedPct}% of today's duties done`,
      icon: CheckCircle2,
      variant: completedPct >= 70 ? ('positive' as const) : ('neutral' as const),span: 'col-span-1',
      showProgress: true,
      progressValue: kpis.dutiesCompleted,
      progressMax: kpis.totalTodayDuties,
    },
    {
      id: 'kpi-total',label: "Today\'s Duties",value: isLoading ? '—' : String(kpis.totalTodayDuties),sub: isLoading? 'Loading…'
        : kpis.lastRefreshed
        ? `Updated ${lastRefreshedLabel}`
        : 'Total scheduled today',icon: Clock,variant: 'neutral' as const,span: 'col-span-1',
      showProgress: false,
    },
    {
      id: 'kpi-bhatta',label: 'Pending Bhatta',
      value: pendingBhatta === null ? '—' : String(pendingBhatta),
      sub: pendingBhatta === null
        ? 'Loading…'
        : pendingBhatta === 0
        ? 'No pending bhatta'
        : `${pendingBhatta} trip${pendingBhatta !== 1 ? 's' : ''} awaiting payment`,
      icon: Banknote,
      variant: bhattaVariant,
      span: 'col-span-1',
      showProgress: false,
    },
    {
      id: 'kpi-violations',
      label: "Violations Today",
      value: violationsToday === null ? '—' : String(violationsToday),
      sub: violationsToday === null
        ? 'Loading…'
        : violationsToday === 0
        ? 'No violations today'
        : `${violationsToday} violation${violationsToday !== 1 ? 's' : ''} logged today`,
      icon: ShieldAlert,
      variant: violationsVariant,
      span: 'col-span-1',
      showProgress: false,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {kpiCards.map((kpi) => {
        const Icon = kpi.icon;
        const cardLoading = isLoading || (kpi.id === 'kpi-bhatta' && pendingBhatta === null) || (kpi.id === 'kpi-violations' && violationsToday === null);
        return (
          <div
            key={kpi.id}
            className={`glass-card rounded-2xl p-5 ${kpi.span} relative overflow-hidden hover-lift border border-[var(--glass-border)]`}
          >
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none rounded-2xl" />

            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${
                    cardLoading ? 'bg-muted/50 text-muted-foreground' : iconBg[kpi.variant]
                  }`}
                >
                  {cardLoading ? (
                    <RefreshCw size={16} className="animate-spin opacity-50" />
                  ) : (
                    <Icon size={18} />
                  )}
                </div>
                <LiveBadge isConnected={isConnected} lastRefreshed={kpis.lastRefreshed} />
              </div>

              <p className="text-[11px] font-600 uppercase tracking-widest text-muted-foreground mb-1.5">
                {kpi.label}
              </p>
              <p
                className={`text-3xl font-800 tabular-nums mb-1 leading-none ${
                  cardLoading ? 'text-muted-foreground' : valueColor[kpi.variant]
                }`}
              >
                {kpi.value}
              </p>
              <p className="text-xs text-muted-foreground">{kpi.sub}</p>

              {kpi.showProgress && !cardLoading && (kpi.progressMax ?? 0) > 0 && (
                <ProgressBar
                  value={kpi.progressValue ?? 0}
                  max={kpi.progressMax ?? 1}
                  colorClass={progressColor[kpi.variant]}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}